import {
  createContext,
  FC,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { addNewMessage } from '../../../redux/store/chatHistory/slice'
import { CHAT_TYPE } from 'constants/common'
import { getUser } from 'redux/store/user/getters'
import {
  getChatDetails,
  getChatMembers,
  getCountOfUnreadMessages
} from 'redux/store/chatDetails/getters'
import { setChatDetailsAsync } from 'redux/store/chatDetails/actions'
import { sendMessageRequest, uploadChatAttachments } from 'features/Chat/api'
import { ChatProps } from 'features/Chat/Chat'
import {
  ChatMember,
  IChatDetails,
  TUploadedAttachments
} from 'redux/store/chatDetails/types'
import { resetUnreadMessageCount } from '../../../redux/store/chats/actions'
import { getChatHistorySearchAsync } from '../../../redux/store/chatHistory/actions'
import { IChatHistory } from '../../../redux/store/chatHistory/types'
import { getChatHistory } from '../../../redux/store/chatHistory/getters'
import {
  resetChatDetails,
  resetChatMembers
} from 'redux/store/chatDetails/slice'
import { updateChatStatusAndOrdering } from 'redux/store/chats/slice'
import { TDocumentTypeUnion } from '../../../components/FileUpload/types'
import { notification } from '../../../components/Notification'
import { BASE_SOCKET_TYPE, VALIDATION_MESSAGES } from '../../../constants'
import { useAppDispatch } from '../../../redux/createStore'
import { AxiosResponse } from 'axios'
import { getLastSocketMessage } from 'redux/store/chatSocket/getters'
import { addCustomLocalMessage } from 'redux/store/chatSocket/slice'

type ContextProps<T extends IChatDetails = IChatDetails> = {
  state: {
    chat: T | undefined | null
    members: ChatMember[]
    isChatOwner: boolean
    canSendMessage: boolean
    isSearch: boolean
    searchValue: string
    firstUnreadMessageId: string | null
    historyRef: MutableRefObject<HTMLDivElement | null>
    currentSearchValue: IChatHistory | null
    uploadedAttachments: TUploadedAttachments[] | null
    currentFile: TUploadedAttachments
  }
  actions: {
    sendMessageRequestAsync: (
      message: string,
      reply?: string,
      attachments?: string[]
    ) => Promise<void>
    handleIsSearch: (value: boolean) => void
    handleSearchValue: (value: string) => void
    handleCurrentSearchValue: (value: IChatHistory) => void
    handleDeleteAttachment: (uuid: string) => Promise<void>
    handleUploadFile: (
      files: File[],
      documentType: TDocumentTypeUnion
    ) => Promise<void>
  }
}

const ChatContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const ChatContextProvider: FC<PropsWithChildren<ChatProps>> = ({
  children,
  ...props
}) => {
  const { id } = props

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const historyRef = useRef<HTMLDivElement | null>(null)

  const user = useSelector(getUser)
  const chat = useSelector(getChatDetails())
  const members = useSelector(getChatMembers)
  const unreadMessages = useSelector(getCountOfUnreadMessages)
  const messages = useSelector(getChatHistory)
  const lastSocketMessage = useSelector(getLastSocketMessage)
  const [isSearch, setIsSearch] = useState(false)
  // state to show fixed unread indicator for unread messages
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<
    string | null
  >(null)
  const [searchValue, setSearchValue] = useState('')
  const [currentSearchValue, setCurrentSearchValue] =
    useState<IChatHistory | null>(null)

  const [uploadedAttachments, setUploadedAttachments] = useState<
    TUploadedAttachments[] | null
  >(null)

  const [currentFile, setCurrentFile] = useState<TUploadedAttachments>({
    file: '',
    uuid: '',
    error: ''
  })

  const timeoutRef = useRef<NodeJS.Timeout>()

  const isChatOwner = useMemo(
    () => user.uuid === chat?.owner_id,
    [chat?.owner_id, user.uuid]
  )
  const handleDeleteAttachment = useCallback(
    async (uuid: string) => {
      uploadedAttachments &&
        setUploadedAttachments(
          uploadedAttachments?.filter((i) => i.uuid !== uuid)
        )
    },
    [uploadedAttachments]
  )

  const handlePercentage = useCallback(
    (val: number) => {
      setCurrentFile((prev) => ({
        file: prev?.file ? prev.file : 'name',
        uuid: prev?.uuid ? prev.uuid : '1',
        percentage: val || 0
      }))
    },
    [setCurrentFile]
  )

  const handleIsSearch = (val: boolean) => {
    setIsSearch(val)
  }

  const handleSearchValue = (val: string) => {
    setSearchValue(val)
  }
  const handleCurrentSearchValue = (val: IChatHistory) => {
    setCurrentSearchValue(val)
  }

  const canSendMessage = useMemo(
    () => (chat?.type === CHAT_TYPE.CHANNEL ? isChatOwner : true),
    [chat?.type, isChatOwner]
  )

  const _listenSearchChange = useCallback(() => {
    if (searchValue !== null) {
      if (searchValue.length) {
        dispatch(getChatHistorySearchAsync(id, { search: searchValue }))
      }
    }
  }, [dispatch, id, searchValue])

  const handleUploadFile = useCallback(
    async (files: File[], documentType: string) => {
      const formData = new FormData()
      formData.append(documentType, files[0], files[0].name)
      setCurrentFile({ ...currentFile, file: files[0].name, uuid: '1' })

      try {
        const response = await uploadChatAttachments(
          {
            document_type: documentType,
            file: formData.get(documentType)
          },
          handlePercentage
        )
        if (!!response?.data) {
          const result = uploadedAttachments || []
          result.push(response?.data)
          setCurrentFile({ file: '', uuid: '' })
          setUploadedAttachments(result)
        }
      } catch (e) {
        const err = e as AxiosResponse
        console.error(err)
        setCurrentFile((prev) => ({ ...prev, error: err?.data?.file[0] }))
      }
    },
    [uploadedAttachments, handlePercentage, currentFile]
  )

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(_listenSearchChange, 500)
  }, [_listenSearchChange])

  const sendMessageRequestAsync = useCallback(
    async (message: string, reply?: string, attachments?: string[]) => {
      try {
        if (!chat?.uuid) {
          throw new Error('Chat ID not provided')
        }

        const result = await sendMessageRequest(chat.uuid, {
          body: message || '',
          attachments,
          reply
        })
        if (result?.data) {
          setUploadedAttachments([])
          setFirstUnreadMessageId(null)
          dispatch(addNewMessage({ message: result.data, chatId: chat.uuid }))
          // this is needed to refresh list of chats with the chat that is currently NOT presented in the list
          dispatch(addCustomLocalMessage({ chatId: chat.uuid }))
          dispatch(
            updateChatStatusAndOrdering({
              uuid: id,
              lastMessageSent: result.data.message.created_at,
              unreadCount: 0
            })
          )
        }
      } catch (e) {
        notification.error({
          message: VALIDATION_MESSAGES.CHAT_DOESNT_EXIST
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chat?.uuid]
  )

  useEffect(() => {
    if (id) {
      dispatch(setChatDetailsAsync(id, navigate))
      setFirstUnreadMessageId(null)
    }
  }, [dispatch, id, navigate])

  // setting first unread message based on message history
  useEffect(() => {
    if (chat?.uuid && messages.length) {
      // we're saving first unread message id to be able display unread label exactly above it
      setFirstUnreadMessageId((prev) => {
        // if unread message wasn't set - need to update it with a new message
        // checking if chat contains any unread messages
        if (!prev) {
          if (prev === null) {
            const reversedMessages = [...messages].reverse()
            // find earliest unread message
            const firstUnreadMessage = reversedMessages.find(
              (message) =>
                message.read_by_members === false &&
                message.message.author.uuid !== user.uuid
            )
            return firstUnreadMessage?.message.uuid ?? ''
          } else {
            // check socket messages and set unread message value if exists for this chat
            if (
              lastSocketMessage?.type === BASE_SOCKET_TYPE.NEW_MESSAGE_ADDED &&
              lastSocketMessage.payload.chat_id === chat.uuid
            ) {
              return lastSocketMessage.payload.message.uuid ?? ''
            }
          }
        }
        return prev
      })
    }
  }, [lastSocketMessage, chat?.uuid, user.uuid, messages])

  // reset unread message count if it not 0 immediately after receiving new message
  useLayoutEffect(() => {
    if (id && messages.length && unreadMessages) {
      dispatch(resetUnreadMessageCount(id))
    }
  }, [dispatch, id, messages.length, unreadMessages])

  useEffect(() => {
    handleIsSearch(false)
    handleSearchValue('')
    dispatch(resetChatDetails())
    dispatch(resetChatMembers())
  }, [dispatch, id])

  const context = useMemo(
    () => ({
      state: {
        chat,
        members,
        isChatOwner,
        canSendMessage,
        isSearch,
        searchValue,
        firstUnreadMessageId,
        historyRef,
        currentSearchValue,
        uploadedAttachments,
        currentFile
      },
      actions: {
        sendMessageRequestAsync,
        handleIsSearch,
        handleSearchValue,
        handleCurrentSearchValue,
        handleUploadFile,
        handleDeleteAttachment
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chat,
      members,
      isChatOwner,
      canSendMessage,
      sendMessageRequestAsync,
      isSearch,
      handleIsSearch,
      firstUnreadMessageId,
      historyRef,
      handleCurrentSearchValue,
      currentSearchValue,
      handleUploadFile,
      uploadedAttachments,
      handleDeleteAttachment,
      currentFile
    ]
  )

  return <ChatContext.Provider value={context}>{children}</ChatContext.Provider>
}

export const useChatContext = <T extends IChatDetails = IChatDetails>() =>
  useContext(ChatContext) as ContextProps<T>

export default ChatContextProvider
